// /status — Pass 5.2 compact list view.
//
// Replaces Pass 5.1's per-workspace-card grid. At 100+
// workspaces the card-per-workspace layout becomes a scroll
// marathon; a tight tabular list ("status broken first") +
// filter/sort/search toolbar + drill-in to a dedicated
// /status/:teamId detail page is the operator-ergonomic shape.
//
// Layout (top → bottom):
//   * Header stripe   (Back link + page title + Refresh button —
//                      structurally identical to Pass 5.1 + TopNav)
//   * Toolbar         (search input + status filter + sort dropdown,
//                      preferences persisted in localStorage)
//   * System section  (Drive integration / Drive queue / Local cache —
//                      whole-system signal, lives ABOVE the workspace
//                      list because it's the bigger-picture context
//                      the operator wants before drilling into any
//                      one workspace)
//   * Workspace list  (rows; click → /status/:teamId)
//
// Position of System section vs list: ABOVE the list. Rationale:
// the queue/cache state is one-shot information the operator
// scans once; the list is the scan-many surface. Putting the
// one-shot info above means the operator's eye doesn't have to
// scroll past 100 rows to see "is the upload pipeline jammed?"
// On wide screens the cards stay readable; on narrow they stack
// before the list and add a small amount of scroll the operator
// can swipe past once.

import { ChevronRight, Folder, Plus, RefreshCw, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { HomeButton } from "../components/HomeButton";
import { SignOutButton } from "../components/SignOutButton";
import { AddWorkspaceModal } from "../components/AddWorkspaceModal";
import { useMemo, useState } from "react";

import { useSystemStatus } from "../state/SystemStatusContext";
import { useAuth } from "../state/AuthContext";
import { useImpersonation } from "../state/ImpersonationContext";
import { gradientCSSFor } from "../lib/avatarGradient";
import type {
  SystemStatusWorkspace,
  DriveQueueDepth,
} from "../lib/api";
import {
  workspaceHealthColor,
  type HealthColor,
} from "../lib/workspaceHealth";
import {
  formatAbsoluteIst,
  formatBytes,
  formatCounts,
  formatRelative,
} from "../lib/timeFmt";
import {
  filterAndSortWorkspaces,
} from "../lib/statusListFilter";
import {
  useStatusListPrefs,
  type StatusFilterKind,
  type StatusSortKind,
} from "../lib/statusListPrefs";
import { ScopeSelector } from "../components/ScopeSelector";
import { DriveQuotaCard } from "../components/DriveQuotaCard";
import { Skeleton } from "../components/ui/Skeleton";
import { EmptyState } from "../components/ui/EmptyState";
import { Card } from "../components/ui/Card";

const DOT_COLOR_CLASS: Record<HealthColor, string> = {
  green: "bg-theme-success-text",
  yellow: "bg-theme-warning-text",
  red: "bg-theme-error-border",
  gray: "bg-theme-text-tertiary",
};

const VAULT_PILL_TEXT_CLASS: Record<string, string> = {
  active: "text-theme-success-text",
  needs_reauth: "text-theme-error-text",
  disabled: "text-theme-error-text",
};

const STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: StatusFilterKind; label: string }> = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "needs_reauth", label: "Needs reauth" },
  { value: "disabled", label: "Disabled" },
];

const SORT_OPTIONS: ReadonlyArray<{ value: StatusSortKind; label: string }> = [
  { value: "severity", label: "Status (broken first)" },
  { value: "name", label: "Name (A–Z)" },
  { value: "polled", label: "Last synced (stalest first)" },
  { value: "last_message", label: "Last message (most recent)" },
];

export function StatusPage() {
  const { status, error, refresh } = useSystemStatus();
  const { role } = useAuth();
  const { current: impersonationTarget } = useImpersonation();
  const roleIsSuperadmin = role === "superadmin";
  // While impersonating, show what the impersonated USER sees: no
  // System diagnostics (their requests are role-downgraded anyway).
  const isSuperadmin = roleIsSuperadmin && impersonationTarget === null;
  // One page, one identity per audience: for an admin this is the
  // SYSTEM STATUS ops console; for a user (or an admin viewing as a
  // user) it is simply their workspaces — the old fixed "System
  // status" title over a workspaces-only page was a mislabel.
  const pageTitle = !roleIsSuperadmin
    ? "My workspaces"
    : impersonationTarget
      ? `${impersonationTarget.username}’s workspaces`
      : "System status";
  const [refreshing, setRefreshing] = useState(false);
  const [showAddWorkspace, setShowAddWorkspace] = useState(false);
  const [prefs, setPrefs] = useStatusListPrefs();

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  };

  const visibleWorkspaces = useMemo(() => {
    if (!status) return [];
    return filterAndSortWorkspaces({
      workspaces: status.workspaces,
      driveQueue: status.drive_queue,
      search: prefs.search,
      status: prefs.status,
      sort: prefs.sort,
    });
  }, [status, prefs]);

  return (
    <div
      data-testid="status-page"
      className="min-h-screen text-theme-text-primary"
    >
      <header className="flex items-center justify-between px-6 py-3 border-b border-theme-border-default">
        <div className="flex items-center gap-4">
          <HomeButton />
          <h1 data-testid="status-title" className="text-h2">
            {pageTitle}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Phase 6.2: impersonation scope — compact pill, no band. */}
          <ScopeSelector />
          {/* Phase 8.2: self-service add-workspace, reachable from the
              status page too (the backend enforces own-account login). */}
          <button
            type="button"
            onClick={() => setShowAddWorkspace(true)}
            data-testid="status-add-workspace"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.1] text-[12px] font-medium text-theme-text-primary hover:border-white/[0.25] transition-colors duration-150"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.75} aria-hidden />
            Add workspace
          </button>
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="status-refresh-now"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.1] text-[12px] font-medium text-theme-text-secondary hover:text-theme-text-primary hover:border-white/[0.2] transition-colors duration-150 disabled:opacity-50"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
              strokeWidth={1.75}
              aria-hidden
            />
            Refresh
          </button>
          <SignOutButton />
        </div>
      </header>


      <main className="px-6 py-6 space-y-6 max-w-6xl mx-auto">
        {error && !status && (
          <div
            data-testid="status-error"
            className="rounded-xl bg-theme-error-bg border border-theme-error-border text-theme-error-text px-4 py-3 text-sm"
          >
            Failed to load /system/status: {error.message} (HTTP {error.status})
          </div>
        )}

        {!status && !error && <StatusLoadingSkeleton />}

        {status && (
          <>
            {/* Phase 8.2: System diagnostics (Drive integration, queue,
                cache, quota) are an ADMIN concern — hidden for regular
                users, who only see their workspaces. */}
            {isSuperadmin && (
              <>
                <SystemSection
                  driveEnabled={status.drive_enabled}
                  driveQueue={status.drive_queue}
                  cacheBytes={status.cache_bytes}
                  cacheCapBytes={status.cache_cap_bytes}
                />
                {/* Drive quota self-fetches /admin/drive-quota; renders
                    nothing for non-superadmins anyway. */}
                <DriveQuotaCard />
              </>
            )}
            <WorkspacesPanel
              total={status.workspaces.length}
              visible={visibleWorkspaces}
              driveQueue={status.drive_queue}
              prefs={prefs}
              onPrefsChange={setPrefs}
            />
          </>
        )}
      </main>

      {showAddWorkspace && (
        <AddWorkspaceModal onClose={() => setShowAddWorkspace(false)} />
      )}
    </div>
  );
}

// ---------- System sub-cards (unchanged from Pass 5.1) --------

interface SystemSectionProps {
  driveEnabled: boolean;
  driveQueue: DriveQueueDepth | null;
  cacheBytes: number | null;
  cacheCapBytes: number | null;
}

function SystemSection({
  driveEnabled,
  driveQueue,
  cacheBytes,
  cacheCapBytes,
}: SystemSectionProps) {
  return (
    <section data-testid="status-system">
      <h2 className="text-h3 mb-3">
        System
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DriveIntegrationCard driveEnabled={driveEnabled} />
        <DriveQueueCard driveQueue={driveQueue} />
        <LocalCacheCard
          cacheBytes={cacheBytes}
          cacheCapBytes={cacheCapBytes}
        />
      </div>
    </section>
  );
}

function DriveIntegrationCard({ driveEnabled }: { driveEnabled: boolean }) {
  return (
    <Card variant="glass" className="px-5 py-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary">
        Drive integration
      </div>
      <div
        data-testid="status-drive-enabled"
        className="mt-2 flex items-center gap-2"
      >
        <span
          className={`inline-block w-2.5 h-2.5 rounded-full ${
            driveEnabled
              ? `${DOT_COLOR_CLASS.green} shadow-[0_0_8px_var(--theme-success-text)]`
              : DOT_COLOR_CLASS.gray
          }`}
        />
        <span className="font-display text-2xl font-bold text-theme-text-primary leading-none">
          {driveEnabled ? "Enabled" : "Disabled"}
        </span>
      </div>
    </Card>
  );
}

function DriveQueueCard({ driveQueue }: { driveQueue: DriveQueueDepth | null }) {
  return (
    <Card variant="glass" className="px-5 py-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary">
        Drive queue
      </div>
      <div data-testid="status-drive-queue" className="mt-2">
        {driveQueue ? (
          <dl className="grid grid-cols-4 gap-x-2 font-mono text-xs">
            <QueueStat label="Queued" value={driveQueue.queued} />
            <QueueStat label="Retrying" value={driveQueue.retrying} />
            <QueueStat
              label="Uploading"
              value={driveQueue.uploading}
              accent={
                driveQueue.uploading > 0 ? "text-theme-success-text" : undefined
              }
            />
            <QueueStat
              label="Dead"
              value={driveQueue.dead}
              testid="status-drive-queue-dead"
              accent={driveQueue.dead > 0 ? "text-theme-error-text" : undefined}
            />
          </dl>
        ) : (
          <div className="font-mono text-xs text-theme-text-tertiary">
            Drive integration disabled
          </div>
        )}
      </div>
    </Card>
  );
}

function QueueStat({
  label,
  value,
  accent,
  testid,
}: {
  label: string;
  value: number;
  accent?: string;
  testid?: string;
}) {
  return (
    <div>
      <dt className="font-mono text-[9px] uppercase tracking-widest text-theme-text-tertiary">
        {label}
      </dt>
      <dd
        data-testid={testid}
        className={`mt-0.5 font-display text-xl font-bold ${accent ?? "text-theme-text-primary"}`}
      >
        {value}
      </dd>
    </div>
  );
}

function LocalCacheCard({
  cacheBytes,
  cacheCapBytes,
}: {
  cacheBytes: number | null;
  cacheCapBytes: number | null;
}) {
  const pct =
    cacheBytes !== null && cacheCapBytes && cacheCapBytes > 0
      ? Math.min(100, Math.round((cacheBytes / cacheCapBytes) * 100))
      : 0;
  return (
    <Card variant="glass" className="px-5 py-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary">
        Local cache
      </div>
      <div className="mt-2" data-testid="status-cache-usage">
        {cacheBytes === null || cacheCapBytes === null ? (
          <div className="font-mono text-xs text-theme-text-tertiary">
            Drive integration disabled
          </div>
        ) : (
          <>
            <div className="font-display text-xl font-bold text-theme-text-primary leading-none">
              {formatBytes(cacheBytes)}{" "}
              <span className="font-mono text-xs font-normal text-theme-text-tertiary">
                / {formatBytes(cacheCapBytes)} ({pct}%)
              </span>
            </div>
            <div className="mt-2.5 h-2 w-full rounded-full bg-white/[0.08] overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${pct}%`, background: "linear-gradient(90deg, #8b6cff, #22d3ee)" }}
              />
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

// ---------- Workspaces panel (list + toolbar) -----------------

interface WorkspacesPanelProps {
  total: number;
  visible: SystemStatusWorkspace[];
  driveQueue: DriveQueueDepth | null;
  prefs: ReturnType<typeof useStatusListPrefs>[0];
  onPrefsChange: ReturnType<typeof useStatusListPrefs>[1];
}

function WorkspacesPanel({
  total,
  visible,
  driveQueue,
  prefs,
  onPrefsChange,
}: WorkspacesPanelProps) {
  return (
    <section data-testid="status-workspaces">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-h3">
          Workspaces
        </h2>
        <span
          data-testid="status-workspace-count"
          className="font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary"
        >
          {visible.length} of {total}
        </span>
      </div>

      <Toolbar prefs={prefs} onPrefsChange={onPrefsChange} />

      {total === 0 ? (
        <EmptyVaultState />
      ) : visible.length === 0 ? (
        <EmptyFilterState
          onClear={() =>
            onPrefsChange((p) => ({ ...p, search: "", status: "all" }))
          }
        />
      ) : (
        <ul
          data-testid="status-workspace-list"
          className="glass-card rounded-2xl overflow-hidden divide-y divide-white/[0.06]"
        >
          {visible.map((w) => (
            <WorkspaceRow key={w.team_id} workspace={w} driveQueue={driveQueue} />
          ))}
        </ul>
      )}
    </section>
  );
}

function EmptyVaultState() {
  return (
    <div
      data-testid="status-empty"
      className="glass-card rounded-2xl"
    >
      <EmptyState
        icon={<Folder className="w-full h-full" strokeWidth={1.5} />}
        title="No workspaces archived yet"
        description="Run the login flow to archive your first Slack workspace."
      />
    </div>
  );
}

function EmptyFilterState({ onClear }: { onClear: () => void }) {
  return (
    <div
      data-testid="status-empty-filter"
      className="glass-card rounded-2xl"
    >
      <EmptyState
        icon={<Search className="w-full h-full" strokeWidth={1.5} />}
        title="No workspaces match your filters"
        description="Try clearing the filters or search."
        action={{ label: "Clear filters", onClick: onClear }}
      />
    </div>
  );
}

// S2.2: layout-preserving skeleton for the initial /system/status
// fetch — three metric-card placeholders + a list of workspace rows.
function StatusLoadingSkeleton() {
  return (
    <div data-testid="status-loading">
      <section className="mb-6">
        <Skeleton variant="text" width="6rem" className="mb-3" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="glass-card rounded-2xl px-5 py-4 space-y-snug"
            >
              <Skeleton variant="text" width="7rem" />
              <Skeleton variant="rectangle" height="1.75rem" />
            </div>
          ))}
        </div>
      </section>
      <Skeleton variant="text" width="8rem" className="mb-3" />
      <div className="glass-card rounded-2xl overflow-hidden divide-y divide-white/[0.06]">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-2.5 bg-white/[0.02]">
            <Skeleton variant="circle" width="0.5rem" height="0.5rem" />
            <Skeleton variant="text" width="14rem" />
            <Skeleton variant="rectangle" width="5rem" height="1.25rem" className="ml-auto" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Toolbar -------------------------------------------

interface ToolbarProps {
  prefs: ReturnType<typeof useStatusListPrefs>[0];
  onPrefsChange: ReturnType<typeof useStatusListPrefs>[1];
}

function Toolbar({ prefs, onPrefsChange }: ToolbarProps) {
  return (
    <div
      data-testid="status-toolbar"
      className="flex flex-col md:flex-row md:items-center gap-3 mb-3 glass-card rounded-2xl px-4 py-3"
    >
      {/* Search — bordered input matching LoginScreen's
          field style + a search icon prefix. */}
      <label className="flex items-center gap-2 flex-1 min-w-0">
        <Search
          className="w-3.5 h-3.5 text-theme-text-tertiary flex-shrink-0"
          strokeWidth={1.5}
          aria-hidden
        />
        <input
          type="search"
          data-testid="status-search-input"
          value={prefs.search}
          onChange={(e) =>
            onPrefsChange((p) => ({ ...p, search: e.target.value }))
          }
          placeholder="Search name or team_id"
          className="w-full bg-transparent border-0 outline-none font-mono text-xs text-theme-text-primary placeholder-theme-text-tertiary"
        />
      </label>

      {/* Status chip group — bordered pills, one selected. */}
      <div
        data-testid="status-filter-group"
        className="flex items-center rounded-full overflow-hidden border border-white/[0.12] bg-white/[0.03]"
        role="radiogroup"
        aria-label="Filter by auth status"
      >
        {STATUS_FILTER_OPTIONS.map((opt, i) => {
          const selected = prefs.status === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              data-testid={`status-filter-${opt.value}`}
              data-selected={selected ? "true" : "false"}
              onClick={() =>
                onPrefsChange((p) => ({ ...p, status: opt.value }))
              }
              className={[
                "px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest",
                "transition-colors duration-150",
                i > 0 ? "border-l border-theme-border-default" : "",
                selected
                  ? "bg-theme-bg-chip-subtle text-theme-text-primary"
                  : "text-theme-text-tertiary hover:text-theme-text-primary",
              ].join(" ")}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* Sort — native select for accessibility + zero new deps. */}
      <label className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary">
          Sort
        </span>
        <select
          data-testid="status-sort-select"
          value={prefs.sort}
          onChange={(e) =>
            onPrefsChange((p) => ({
              ...p,
              sort: e.target.value as StatusSortKind,
            }))
          }
          className="rounded-lg bg-white/[0.04] border border-white/[0.12] px-2.5 py-1.5 font-mono text-xs text-theme-text-secondary"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

// ---------- Row -----------------------------------------------

interface WorkspaceRowProps {
  workspace: SystemStatusWorkspace;
  driveQueue: DriveQueueDepth | null;
}

function WorkspaceRow({ workspace: w, driveQueue }: WorkspaceRowProps) {
  const navigate = useNavigate();
  const { role } = useAuth();
  const isSuperadmin = role === "superadmin";
  const color = workspaceHealthColor({ workspace: w, driveQueue });
  const vaultClass =
    VAULT_PILL_TEXT_CLASS[w.vault_status] ?? "text-theme-text-secondary";

  const onActivate = () => navigate(`/status/${encodeURIComponent(w.team_id)}`);

  return (
    <li
      data-testid={`status-row-${w.team_id}`}
      className="bg-transparent hover:bg-white/[0.05] transition-colors duration-150"
    >
      <button
        type="button"
        onClick={onActivate}
        className="w-full text-left px-4 py-2.5 flex items-center gap-3"
        aria-label={`${w.team_name} — open detail`}
      >
        {/* Status dot. */}
        <span
          data-testid={`status-dot-${w.team_id}`}
          data-color={color}
          className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${DOT_COLOR_CLASS[color]}`}
        />

        {/* Aurora: workspace gradient bubble (same family app-wide). */}
        <span
          aria-hidden
          style={{ background: gradientCSSFor(w.team_name || w.team_id) }}
          className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-white"
        >
          {(w.team_name || w.team_id).slice(0, 2).toUpperCase()}
        </span>

        {/* Name + team_id. min-w-0 + truncate keeps the row
            single-line even with very long names. */}
        <div className="flex-1 min-w-0">
          <div className="font-sans text-sm text-theme-text-primary truncate">
            {w.team_name}
          </div>
          <div className="font-mono text-[10px] text-theme-text-muted truncate">
            {w.team_id}
            {isSuperadmin && (
              <span
                data-testid={`status-owner-${w.team_id}`}
                className="text-theme-text-tertiary"
              >
                {" · "}
                {w.owned_by_username ?? "unknown"}
              </span>
            )}
          </div>
        </div>

        {/* Vault pill. */}
        <span
          data-testid={`status-vault-pill-${w.team_id}`}
          className={`hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full bg-white/[0.05] border border-white/[0.12] font-mono text-[10px] uppercase tracking-widest ${vaultClass}`}
        >
          {w.vault_status}
        </span>

        {/* Polled. Relative inline; IST below in muted text
            (matches the detail card's timestamp treatment). */}
        <div
          className="hidden md:block w-28 text-right"
          title={
            w.last_polled_at
              ? `last synced ${formatAbsoluteIst(w.last_polled_at)}`
              : "never synced"
          }
        >
          <div className="font-mono text-xs text-theme-text-secondary">
            {formatRelative(w.last_polled_at)}
          </div>
          <div className="font-mono text-[10px] text-theme-text-muted">
            last synced
          </div>
        </div>

        {/* Last message. */}
        <div
          className="hidden md:block w-28 text-right"
          title={
            w.last_message_at
              ? `last message ${formatAbsoluteIst(w.last_message_at)}`
              : "no messages"
          }
        >
          <div className="font-mono text-xs text-theme-text-secondary">
            {formatRelative(w.last_message_at)}
          </div>
          <div className="font-mono text-[10px] text-theme-text-muted">
            last msg
          </div>
        </div>

        {/* Counts. */}
        <div className="hidden lg:block w-40 text-right font-mono text-[11px] text-theme-text-secondary">
          {formatCounts(w.message_count, w.attachment_count)}
        </div>

        {/* Drill-in chevron. */}
        <ChevronRight
          className="w-4 h-4 text-theme-text-tertiary flex-shrink-0"
          strokeWidth={1.5}
          aria-hidden
        />
      </button>
    </li>
  );
}
